// src/pages/RidersPage.jsx

import React, { useState, useCallback } from 'react';
import { usePolling } from '../hooks/usePolling';
import { useNavigate } from 'react-router-dom';
import { ridersAPI, ordersAPI } from '../services/api';
import { LoadingOverlay, EmptyState, StatusBadge, toast } from '../components/UI';

const RIDER_STATUS_CONFIG = {
  Available: { color: 'var(--success)', bg: 'var(--success-bg)', label: 'Available' },
  Busy: { color: 'var(--warning)', bg: 'var(--warning-bg)', label: 'Busy' },
  Offline: { color: 'var(--gray-400)', bg: 'var(--gray-100)', label: 'Offline' },
};

export default function RidersPage() {
  const navigate = useNavigate();
  const [riders, setRiders] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [r, o] = await Promise.all([ridersAPI.getAll(), ordersAPI.getAll()]);
      setRiders(r || []);
      setOrders(o || []);
    } catch { toast.error('Failed to load riders.'); }
    finally { setLoading(false); }
  }, []);

  usePolling(fetchData, 10000);

  if (loading) return <LoadingOverlay message="Loading riders…" />;

  // Build per-rider order counts
  const riderOrderMap = {};
  orders.forEach(o => {
    if (o.rider_id) {
      if (!riderOrderMap[o.rider_id]) riderOrderMap[o.rider_id] = { active: 0, delivered: 0, failed: 0 };
      if (!['Delivered', 'Failed'].includes(o.status)) riderOrderMap[o.rider_id].active++;
      if (o.status === 'Delivered') riderOrderMap[o.rider_id].delivered++;
      if (o.status === 'Failed') riderOrderMap[o.rider_id].failed++;
    }
  });

  const available = riders.filter(r => r.status === 'Available').length;
  const busy = riders.filter(r => r.status === 'Busy').length;

  return (
    <div className="animate-fade-in">
      <div className="page-header">
        <h1 className="page-title">Riders</h1>
        <p className="page-sub">{riders.length} total · {available} available · {busy} busy</p>
      </div>

      {/* ─── Summary Cards ───────────────────────────────────────────────── */}
      <div className="stat-grid" style={{ marginBottom: 24 }}>
        {[
          { label: 'Total Riders', value: riders.length, cls: 'accent' },
          { label: 'Available', value: available, cls: 'success' },
          { label: 'Busy / On Delivery', value: busy, cls: 'info' },
          { label: 'Offline', value: riders.filter(r => r.status === 'Offline').length, cls: 'danger' },
        ].map((s, i) => (
          <div key={s.label} className={`stat-card ${s.cls} animate-fade-up delay-${i + 1}`}>
            <div className="stat-label">{s.label}</div>
            <div className="stat-value">{s.value}</div>
            <div className="stat-icon">{s.icon}</div>
          </div>
        ))}
      </div>

      {/* ─── Riders Grid ─────────────────────────────────────────────────── */}
      {riders.length === 0 ? (
        <EmptyState icon="" title="No riders found" subtitle="Riders will appear here once added by an admin." />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
          {riders.map((rider, i) => {
            const stats = riderOrderMap[rider.user_id] || { active: 0, delivered: 0, failed: 0 };
            const cfg = RIDER_STATUS_CONFIG[rider.status] || RIDER_STATUS_CONFIG.Offline;
            const successRate = (stats.delivered + stats.failed) > 0
              ? Math.round((stats.delivered / (stats.delivered + stats.failed)) * 100)
              : null;

            return (
              <div
                key={rider.user_id}
                className={`card animate-fade-up delay-${Math.min(i + 1, 6)}`}
                style={{ overflow: 'hidden', cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s' }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = 'var(--shadow-md)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}
                onClick={() => navigate(`/orders?rider=${rider.user_id}`)}
              >
                {/* Status stripe */}
                <div style={{ height: 4, background: cfg.color }} />

                <div style={{ padding: '18px 20px' }}>
                  {/* Header */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
                    <div style={{
                      width: 48, height: 48, borderRadius: '50%',
                      background: 'var(--primary)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 20, fontWeight: 800, color: 'var(--accent)',
                      flexShrink: 0,
                    }}>
                      {rider.name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <div style={{ flex: 1, overflow: 'hidden' }}>
                      <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--gray-900)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {rider.name}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--gray-400)', fontFamily: 'var(--font-mono)' }}>
                        {rider.phone}
                      </div>
                    </div>
                    <div style={{
                      padding: '4px 10px', borderRadius: 99,
                      background: cfg.bg, color: cfg.color,
                      fontSize: 11.5, fontWeight: 700,
                    }}>
                      {cfg.label}
                    </div>
                  </div>

                  {/* Stats */}
                  <div style={{
                    display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
                    gap: 8, paddingTop: 14,
                    borderTop: '1px solid var(--gray-100)',
                  }}>
                    {[
                      { label: 'Active', value: stats.active, color: 'var(--info)' },
                      { label: 'Delivered', value: stats.delivered, color: 'var(--success)' },
                      { label: 'Failed', value: stats.failed, color: 'var(--danger)' },
                    ].map(s => (
                      <div key={s.label} style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 20, fontWeight: 800, color: s.color, fontFamily: 'var(--font-display)' }}>
                          {s.value}
                        </div>
                        <div style={{ fontSize: 10.5, color: 'var(--gray-400)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          {s.label}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Success rate bar */}
                  {successRate !== null && (
                    <div style={{ marginTop: 14 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--gray-400)', marginBottom: 5 }}>
                        <span>Success Rate</span>
                        <span style={{ fontWeight: 700, color: successRate > 75 ? 'var(--success)' : 'var(--warning)' }}>
                          {successRate}%
                        </span>
                      </div>
                      <div style={{ height: 5, background: 'var(--gray-100)', borderRadius: 99, overflow: 'hidden' }}>
                        <div style={{
                          height: '100%', borderRadius: 99,
                          width: `${successRate}%`,
                          background: successRate > 75 ? 'var(--success)' : 'var(--warning)',
                          transition: 'width 0.6s ease',
                        }} />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}